import React, { useEffect } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { useParams } from 'react-router';
import { Meteor } from 'meteor/meteor';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Docmnt, ModelClass, RefMap } from '/imports/types';
import { useUser, useMethod } from '/imports/hooks';
import { Icon } from '/imports/ui/Icon';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { FormSpec, autoForm, formConfig } from '/imports/ui/AutoForm';

const ModelFormPageStyled = styled(Page)`
  form {
    padding-bottom: 3rem;
  }
`;

interface ModelFormPageProps<M extends Docmnt, R extends RefMap<M> = any> {
  _id?: string;
  className?: string;
  modelClass: ModelClass<M, R>;
  formSpec: FormSpec<M> | (() => FormSpec<M>);
  formDeps?: any[];
  permission?: (user?: Meteor.User) => boolean | undefined;
  update?: Partial<M>;
  setUpdate?: (value: React.SetStateAction<Partial<M>>) => void;
  defaultUpdate?: Partial<M>;
  documentRef?: React.MutableRefObject<M | undefined>;
  setDocument?: (value?: M) => void;
  updateRef?: React.MutableRefObject<Partial<M> | undefined>;
  setUpdateRef?: React.MutableRefObject<((value: React.SetStateAction<Partial<M>>) => void) | undefined>;
  pageHeading?: React.ReactNode;
  header?: React.ReactNode | ((setUpdate: (value: React.SetStateAction<Partial<M>>) => void, update: Partial<M>, error: any) => React.ReactNode);
  footer?: React.ReactNode | ((setUpdate: (value: React.SetStateAction<Partial<M>>) => void, update: Partial<M>, error: any) => React.ReactNode);
  styled?: any;
  redirect?: string | null;
  pageHeader?: React.ReactNode;
  pageFooter?: React.ReactNode;
  beforeSave?: (update: Partial<M>, document?: M) => boolean | Promise<boolean>;
  onSave?: (_id: string) => void,
  onError?: (error: any) => void,
  cacheKey?: string,
  submitButtonText?: string;
  extraButtons?: React.ReactNode;
}

/** Generic create/edit page that renders an AutoForm for a model class. */
export const ModelFormPage = <M extends Docmnt>(props: ModelFormPageProps<M>) => {
  const { modelClass, className, formSpec, permission, setUpdate, update, defaultUpdate, documentRef, setDocument, updateRef, setUpdateRef,
    header, footer, formDeps, styled, pageHeading, redirect, beforeSave, onSave, onError, pageHeader, pageFooter, cacheKey, submitButtonText, extraButtons } = props;
  const { getMethod, createMethod, updateMethod, deleteMethod,
    pathPrefix, modelName, pluralName, icon } = modelClass;
  if (!getMethod || !createMethod || !updateMethod || !pathPrefix || !pluralName || !modelName) {
    throw `modelClass is misconfigured for ModelFormPage: ${modelClass.collection}`;
  }
  const user = useUser();
  const { _id } = props._id ? { _id: props._id } : useParams<any>();
  const isNew = _id === 'new';
  const [document, documentLoading] = isNew ? [undefined, false] : useMethod(getMethod, _id || 'new', [_id]);
  const form = formConfig<M>(formSpec instanceof Array ? formSpec : formSpec(), [update, document, ...(formDeps || [])]);
  const [Form, _setUpdate, _update] = autoForm({
    document,
    createMethod,
    updateMethod,
    deleteMethod,
    form,
    update,
    setUpdate,
    defaultUpdate,
    header,
    footer,
    redirect: redirect === undefined ? pathPrefix : redirect,
    beforeSave,
    onSave,
    onError,
    cacheKey,
    submitButtonText,
    extraButtons,
  });
  useEffect(() => {
    if (setUpdateRef) setUpdateRef.current = _setUpdate;
  }, [_setUpdate]);
  useEffect(() => {
    if (updateRef) updateRef.current = _update;
  }, [_update]);
  useEffect(() => {
    if (documentRef) documentRef.current = document;
    setDocument?.(document);
  }, [document]);
  const stringName = modelClass.asString && document ? modelClass.asString(document) : document?.name || document?._id || '';
  if (documentLoading) return <LoadingFlex />;
  if ((permission && !permission?.(user)) && user && !Meteor.loggingIn()) return <NotPermittedPage />;
  return React.createElement(styled || ModelFormPageStyled, {
    className: `page${documentLoading ? ' initializing' : ''} ${className || ''}`,
    breadcrumbs: [
      { path: pathPrefix, label: pluralName },
      { path: `${pathPrefix}/${_id}`, label: !document ? strings.New() : stringName },
    ]
  },
  <>
    {
      pageHeading ||
        <PageHeading
          left={
            <h1>
              {icon && <Icon icon={icon} /> || null}
              {isNew ? `${strings.New()} ${modelName}` : `${strings.Edit()} ${modelName}`}
            </h1>
          }
        />
    }
    <PageBody>
      {pageHeader || null}
      {Form}
      {pageFooter || null}
    </PageBody>
  </>
  );
};